import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { InstallBanner } from '../components/InstallBanner';
import { Mail, Lock, User, UserPlus, Loader2, GraduationCap, BookOpen } from 'lucide-react';
import SocialAuthButtons from '../components/SocialAuthButtons';

export default function RegisterPage() {
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [role, setRole] = useState('student');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const { register } = useAuth();
    const navigate = useNavigate();

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        if (password !== confirmPassword) {
            setError('Passwords do not match');
            return;
        }

        if (password.length < 6) {
            setError('Password must be at least 6 characters');
            return;
        }

        setLoading(true);

        const result = await register(name, email, password, role);

        if (result.success) {
            navigate('/dashboard');
        } else {
            setError(result.error);
        }
        setLoading(false);
    };

    return (
        <div className="min-h-screen flex">
            {/* Left side - Image */}
            <div 
                className="hidden lg:flex lg:w-1/2 items-center justify-center p-12"
                style={{
                    backgroundImage: 'url(https://static.prod-images.emergentagent.com/jobs/ad72222b-8ef5-4b27-9c5b-21debe554c06/images/40d10c6b1137089e0d3a8ddda2794d0d0abb2fe4ac2a6b914adbeb630e7a8628.png)',
                    backgroundSize: 'cover',
                    backgroundPosition: 'center'
                }}
            >
                <div className="brutalist-card p-8 max-w-md">
                    <h2 className="text-3xl font-black tracking-tight mb-4">Join A-Level AI Grader</h2>
                    <p className="text-lg text-zinc-600">Start getting expert feedback on your Business, Economics, and Maths answers today.</p>
                </div>
            </div>

            {/* Right side - Form */}
            <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-[#F8F7FC]">
                <div className="w-full max-w-md">
                    <InstallBanner />
                    <div className="mb-8">
                        <h1 className="text-4xl font-black tracking-tight text-zinc-950 mb-2">Create account</h1>
                        <p className="text-zinc-600">Sign up to start improving your exam answers</p>
                    </div>

                    <form onSubmit={handleSubmit} className="space-y-5">
                        {error && (
                            <div className="brutalist-card p-4 bg-red-50 border-red-500" data-testid="register-error">
                                <p className="text-red-700 font-medium">{error}</p>
                            </div>
                        )}

                        <div>
                            <label className="block text-sm font-semibold text-zinc-700 mb-2">
                                I am a
                            </label>
                            <div className="grid grid-cols-2 gap-3" data-testid="register-role-toggle">
                                <button
                                    type="button"
                                    onClick={() => setRole('student')}
                                    className={`flex items-center justify-center gap-2 py-3 rounded-lg border-2 font-bold text-sm transition-all ${
                                        role === 'student'
                                            ? 'border-[#7C3AED] bg-[#F4ECFF] text-[#7C3AED]'
                                            : 'border-zinc-200 bg-white text-zinc-500 hover:border-zinc-300'
                                    }`}
                                    data-testid="register-role-student"
                                >
                                    <GraduationCap className="w-4 h-4" />
                                    Student
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setRole('teacher')}
                                    className={`flex items-center justify-center gap-2 py-3 rounded-lg border-2 font-bold text-sm transition-all ${
                                        role === 'teacher'
                                            ? 'border-[#7C3AED] bg-[#F4ECFF] text-[#7C3AED]'
                                            : 'border-zinc-200 bg-white text-zinc-500 hover:border-zinc-300'
                                    }`}
                                    data-testid="register-role-teacher"
                                >
                                    <BookOpen className="w-4 h-4" />
                                    Teacher
                                </button>
                            </div>
                        </div>

                        <div>
                            <label className="block text-sm font-semibold text-zinc-700 mb-2">
                                Full Name
                            </label>
                            <div className="relative">
                                <User className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-zinc-400" />
                                <input
                                    type="text"
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    className="brutalist-input has-leading-icon"
                                    placeholder="John Doe"
                                    required
                                    data-testid="register-name-input"
                                />
                            </div>
                        </div>

                        <div>
                            <label className="block text-sm font-semibold text-zinc-700 mb-2">
                                Email Address
                            </label>
                            <div className="relative">
                                <Mail className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-zinc-400" />
                                <input
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    className="brutalist-input has-leading-icon"
                                    placeholder="you@example.com"
                                    required
                                    data-testid="register-email-input"
                                />
                            </div>
                        </div>

                        <div>
                            <label className="block text-sm font-semibold text-zinc-700 mb-2">
                                Password
                            </label>
                            <div className="relative">
                                <Lock className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-zinc-400" />
                                <input
                                    type="password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    className="brutalist-input has-leading-icon"
                                    placeholder="••••••••"
                                    required
                                    data-testid="register-password-input"
                                />
                            </div>
                        </div>

                        <div>
                            <label className="block text-sm font-semibold text-zinc-700 mb-2">
                                Confirm Password
                            </label>
                            <div className="relative">
                                <Lock className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-zinc-400" />
                                <input
                                    type="password"
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    className="brutalist-input has-leading-icon"
                                    placeholder="••••••••"
                                    required
                                    data-testid="register-confirm-password-input"
                                />
                            </div>
                        </div>

                        <button
                            type="submit"
                            disabled={loading}
                            className="brutalist-btn-primary w-full flex items-center justify-center gap-2"
                            data-testid="register-submit-button"
                        >
                            {loading ? (
                                <>
                                    <Loader2 className="w-5 h-5 animate-spin" />
                                    Creating account...
                                </>
                            ) : (
                                <>
                                    <UserPlus className="w-5 h-5" />
                                    Create Account
                                </>
                            )}
                        </button>
                    </form>

                    <SocialAuthButtons mode="signup" />

                    <div className="mt-8 text-center">
                        <p className="text-zinc-600">
                            Already have an account?{' '}
                            <Link to="/login" className="font-semibold text-zinc-950 underline hover:no-underline" data-testid="login-link">
                                Sign in
                            </Link>
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
}
